import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { useDispatch } from "react-redux";
import { logout } from "../../redux/userRedux";
import Navbar from "../../components/Navbar";
import NoteCard from "../../components/NoteCard";
import Comment from "../../components/Comment";
import OnlineDot from "../../components/OnlineDot";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { publicRequest } from "../../requestMethods";
import theme from "../../theme";
import { mobile } from "../../responsive";
import {
  IconDocument, IconFolder, IconLogOut, IconUsers, IconX, IconCheck,
} from "../../icons";
import communityImg from "../../assets/community.png";
import ltceLogo from "../../assets/ltce.png";
import ltceBannerImg from "../../assets/banner.png";

const STATIC_URL = (process.env.REACT_APP_BASE_URL || "http://localhost:8000/api").replace("/api", "");

const fadeUp = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;
const scaleIn = keyframes`from{opacity:0;transform:scale(0.96)}to{opacity:1;transform:scale(1)}`;

/* ── App shell ── */
const Shell = styled.div`display:flex;min-height:100vh;background:${theme.bg};`;

const Sidebar = styled.aside`
  width:${theme.sidebarWidth};flex-shrink:0;background:${theme.sidebarBg};
  border-right:1px solid ${theme.separator};display:flex;flex-direction:column;
  position:fixed;top:0;left:0;height:100vh;z-index:60;overflow-y:auto;
  ${mobile("display:none;")}
`;
const SidebarTop = styled.div`padding:22px 18px 16px;border-bottom:1px solid ${theme.separator};`;
const LogoRow = styled(Link)`display:flex;align-items:center;gap:10px;text-decoration:none;`;
const LogoMark = styled.div`
  width:36px;height:36px;background:${theme.primary};color:#fff;border-radius:9px;
  display:flex;align-items:center;justify-content:center;font-size:17px;font-weight:800;
  font-family:Georgia,serif;flex-shrink:0;
`;
const LogoText = styled.div`font-size:14px;font-weight:700;color:${theme.textMain};line-height:1.2;`;
const Nav = styled.nav`padding:14px 10px 6px;display:flex;flex-direction:column;gap:2px;flex:1;`;
const NavItem = styled(Link)`
  display:flex;align-items:center;gap:10px;padding:9px 12px;border-radius:8px;font-size:13px;
  font-weight:${p=>p.$active?"700":"500"};color:${p=>p.$active?theme.primary:theme.textMuted};
  background:${p=>p.$active?theme.primaryLight:"transparent"};text-decoration:none;
  transition:background .15s,color .15s;
  &:hover{background:${theme.primaryLight};color:${theme.primary};}
`;
const CommunityMainNavItem = styled(NavItem)`justify-content:flex-start;`;
const CommunityChevron = styled.span`
  width:7px;height:7px;display:inline-block;
  border-right:1.8px solid currentColor;border-bottom:1.8px solid currentColor;
  transform:${p=>p.$open?"rotate(-135deg)":"rotate(45deg)"};
  margin-top:${p=>p.$open?"2px":"-1px"};
  transition:transform .15s ease,margin-top .15s ease;
`;
const CommunityChevronHit = styled.span`
  margin-left:auto;
  width:24px;
  height:24px;
  border-radius:6px;
  display:flex;
  align-items:center;
  justify-content:center;
  transition:background .15s;
  &:hover{background:${theme.primaryLight};}
`;
const CommunitySubList = styled.div`
  margin:4px 0 4px 8px;
  display:flex;
  flex-direction:column;
  gap:2px;
`;
const CommunitySubItem = styled.button`
  width:100%;
  border:none;
  border-radius:8px;
  background:${p=>p.$active?theme.primaryLight:"transparent"};
  color:${p=>p.$active?theme.primary:theme.textMuted};
  padding:7px 10px;
  font-size:12px;
  font-weight:${p=>p.$active?"700":"600"};
  font-family:inherit;
  cursor:pointer;
  display:flex;
  align-items:center;
  gap:8px;
  text-align:left;
  transition:background .15s,color .15s;
  &:hover{background:${theme.primaryLight};color:${theme.primary};}
`;
const CommunitySubAvatar = styled.img`
  width:18px;height:18px;border-radius:5px;object-fit:cover;flex-shrink:0;
`;
const SidebarUser = styled.div`padding:14px 16px;border-top:1px solid ${theme.separator};display:flex;align-items:center;gap:10px;`;
const UserAvatar = styled.div`
  width:34px;height:34px;border-radius:50%;background:${theme.bgAlt};border:1.5px solid ${theme.cardBorder};
  overflow:hidden;flex-shrink:0;display:flex;align-items:center;justify-content:center;
  font-size:13px;font-weight:700;color:${theme.primary};cursor:pointer;
`;
const UserAvatarImg = styled.img`width:100%;height:100%;object-fit:cover;`;
const UserInfo = styled.div`flex:1;min-width:0;`;
const UserName = styled.div`font-size:12.5px;font-weight:700;color:${theme.textMain};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`;
const UserRole = styled.div`font-size:10px;font-weight:600;color:${theme.textMuted};text-transform:uppercase;letter-spacing:.06em;`;
const LogoutBtn = styled.button`
  background:transparent;border:none;padding:4px;cursor:pointer;color:${theme.danger};
  display:flex;align-items:center;border-radius:6px;flex-shrink:0;opacity:.7;
  transition:opacity .15s,background .15s;&:hover{opacity:1;background:${theme.dangerLight};}
`;

/* ── Main area ── */
const Main = styled.div`margin-left:${theme.sidebarWidth};flex:1;display:flex;flex-direction:column;min-width:0;${mobile("margin-left:0;")}`;
const PageArea = styled.div`padding:32px 32px 80px;${mobile("padding:20px 14px 80px;")}`;
const PageMeta = styled.div`font-size:10.5px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:${theme.primary};margin-bottom:6px;`;
const PageHeading = styled.h1`font-size:26px;font-weight:800;color:${theme.textMain};margin:0 0 6px;letter-spacing:-.5px;`;
const PageSub = styled.p`font-size:13.5px;color:${theme.textMuted};margin:0 0 32px;`;

/* ── Community list grid ── */
const Grid = styled.div`
  display:grid;grid-template-columns:repeat(3,1fr);gap:20px;animation:${fadeUp} .3s ease both;
  @media(max-width:900px){grid-template-columns:repeat(2,1fr);}
  ${mobile("grid-template-columns:1fr;")}
`;
const CommunityCard = styled.div`
  background:${theme.cardBg};border:1.5px solid ${p=>p.$active?theme.primary:theme.cardBorder};
  border-radius:14px;overflow:hidden;cursor:pointer;
  transition:box-shadow .18s,transform .15s,border-color .15s;
  box-shadow:${p=>p.$active?`0 0 0 3px ${theme.primaryGlow}`:"none"};
  &:hover{box-shadow:0 6px 24px rgba(26,18,8,.1);transform:translateY(-2px);border-color:${theme.primary};}
`;
const CardBody = styled.div`padding:13px 15px 15px;`;
const CardTop = styled.div`display:flex;align-items:center;gap:12px;margin-bottom:9px;`;
const CardLogoWrap = styled.div`
  width:52px;height:52px;border-radius:10px;overflow:hidden;background:#fff;
  border:1.5px solid ${theme.cardBorder};flex-shrink:0;
`;
const CardLogo = styled.img`width:100%;height:100%;object-fit:contain;padding:4px;background:#fff;`;
const CardInstitution = styled.div`font-size:10px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:${theme.primary};margin-bottom:3px;`;
const CardName = styled.div`font-size:14px;font-weight:800;color:${theme.textMain};margin-bottom:6px;letter-spacing:-.2px;`;
const CardMeta = styled.div`font-size:12px;color:${theme.textMuted};display:flex;align-items:center;gap:6px;`;
const MemberBadge = styled.span`
  display:inline-flex;align-items:center;gap:4px;background:${theme.primaryLight};
  color:${theme.primary};border-radius:20px;padding:2px 9px;font-size:11px;font-weight:700;
`;
const CardMemberStack = styled.div`
  display:flex;
  align-items:center;
`;
const CardMemberAvatar = styled.div`
  width:24px;
  height:24px;
  border-radius:50%;
  border:2px solid ${theme.cardBg};
  background:${theme.bgAlt};
  color:${theme.primary};
  font-size:10px;
  font-weight:700;
  display:flex;
  align-items:center;
  justify-content:center;
  margin-left:-8px;
  overflow:hidden;

  &:first-child { margin-left:0; }
`;
const CardMemberAvatarImg = styled.img`
  width:100%;
  height:100%;
  object-fit:cover;
`;
const CardMemberMore = styled(CardMemberAvatar)`
  background:#f4d8bb;
  color:${theme.textMain};
`;
const JoinedMeta = styled.div`
  margin-left:auto;
  display:flex;
  align-items:center;
  gap:6px;
`;
const JoinedText = styled.span`
  font-size:11px;
  color:${theme.success};
  font-weight:700;
`;

/* ── Your community banner ── */
const YourCommunityBanner = styled.div`
  background:linear-gradient(135deg,${theme.primary} 0%,${theme.primaryDark} 100%);
  border-radius:14px;padding:20px 24px;margin-bottom:28px;display:flex;align-items:center;
  gap:16px;cursor:pointer;transition:opacity .15s;&:hover{opacity:.93;}
`;
const BannerImg = styled.img`width:56px;height:56px;border-radius:12px;object-fit:contain;padding:4px;background:#fff;border:2px solid rgba(255,255,255,.3);flex-shrink:0;`;
const BannerInfo = styled.div`flex:1;min-width:0;`;
const BannerLabel = styled.div`font-size:10px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:rgba(255,255,255,.7);margin-bottom:3px;`;
const BannerName = styled.div`font-size:18px;font-weight:800;color:#fff;letter-spacing:-.3px;`;
const BannerMembers = styled.div`font-size:12px;color:rgba(255,255,255,.75);margin-top:2px;`;
const BannerAction = styled.div`font-size:12px;font-weight:700;color:rgba(255,255,255,.85);background:rgba(255,255,255,.15);border-radius:8px;padding:7px 14px;flex-shrink:0;`;

const StatusText = styled.p`font-size:14px;color:${p=>p.$error?theme.danger:theme.textMuted};text-align:center;padding:60px 0;`;

/* ── Detail view ── */
const DetailShell = styled.div`display:flex;gap:24px;animation:${scaleIn} .2s ease both;align-items:flex-start;`;

const DetailMain = styled.div`flex:1;min-width:0;`;

const DetailHeader = styled.div`
  background:${theme.cardBg};border:1.5px solid ${theme.cardBorder};border-radius:14px;
  overflow:hidden;margin-bottom:24px;
`;
const DetailHeaderImg = styled.div`width:100%;height:160px;overflow:hidden;background:${theme.bgAlt};position:relative;`;
const DetailHeaderImgEl = styled.img`width:100%;height:100%;object-fit:cover;`;
const DetailHeaderOverlay = styled.div`
  position:absolute;inset:0;background:linear-gradient(to bottom,transparent 40%,rgba(26,18,8,.55));
`;
const DetailHeaderBody = styled.div`padding:18px 20px 20px;display:flex;align-items:flex-start;gap:14px;`;
const DetailHeaderInfo = styled.div`flex:1;min-width:0;`;
const DetailTitleRow = styled.div`display:flex;align-items:center;gap:10px;margin-bottom:6px;`;
const DetailLogo = styled.img`
  width:38px;height:38px;border-radius:8px;object-fit:contain;padding:3px;background:#fff;
  border:1.5px solid ${theme.cardBorder};flex-shrink:0;
`;
const DetailInstitution = styled.div`font-size:10px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:${theme.primary};margin-bottom:4px;`;
const DetailTitle = styled.h2`font-size:20px;font-weight:800;color:${theme.textMain};margin:0;letter-spacing:-.3px;`;
const DetailDesc = styled.p`font-size:13px;color:${theme.textMuted};margin:0;line-height:1.55;`;
const DetailActions = styled.div`display:flex;gap:8px;flex-shrink:0;`;
const JoinBtn = styled.button`
  background:${p=>p.$joined?theme.bgAlt:theme.primary};color:${p=>p.$joined?theme.textMuted:"#fff"};
  border:1.5px solid ${p=>p.$joined?theme.cardBorder:theme.primary};border-radius:9px;
  padding:9px 18px;font-size:13px;font-weight:700;font-family:inherit;cursor:pointer;
  display:flex;align-items:center;gap:6px;transition:background .15s,transform .1s;
  &:hover:not(:disabled){background:${p=>p.$joined?theme.bgAlt:theme.primaryDark};transform:translateY(-1px);}
  &:disabled{opacity:.6;cursor:not-allowed;}
`;
const BackBtn = styled.button`
  background:${theme.bg};border:1.5px solid ${theme.cardBorder};border-radius:9px;
  padding:9px 14px;cursor:pointer;color:${theme.textMuted};display:flex;align-items:center;gap:6px;
  font-size:13px;font-weight:600;font-family:inherit;transition:background .15s;
  &:hover{background:${theme.bgAlt};}
`;

const SectionLabel = styled.div`font-size:10.5px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:${theme.textMuted};margin-bottom:14px;`;

const NotesGrid = styled.div`
  display:flex;flex-direction:column;gap:16px;
`;

/* ── Members sidebar ── */
const MembersSidebar = styled.div`
  width:240px;flex-shrink:0;
  ${mobile("display:none;")}
`;
const MembersCard = styled.div`
  background:${theme.cardBg};border:1.5px solid ${theme.cardBorder};border-radius:14px;
  padding:16px;position:sticky;top:24px;
`;
const MembersTitle = styled.div`font-size:10.5px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:${theme.textMuted};margin-bottom:14px;`;
const MemberList = styled.div`display:flex;flex-direction:column;gap:10px;`;
const MemberRow = styled.div`
  display:flex;align-items:center;gap:10px;cursor:pointer;border-radius:8px;
  padding:6px 8px;margin:-6px -8px;transition:background .12s;
  &:hover{background:${theme.bg};}
`;
const MemberAvatar = styled.div`
  width:34px;height:34px;border-radius:50%;background:${theme.bgAlt};
  border:1.5px solid ${theme.cardBorder};overflow:visible;flex-shrink:0;
  display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:${theme.primary};
  position:relative;
  & > img { width:100%;height:100%;object-fit:cover;border-radius:50%; }
`;
const MemberAvatarImg = styled.img`width:100%;height:100%;object-fit:cover;`;
const MemberInfo = styled.div`flex:1;min-width:0;`;
const MemberName = styled.div`font-size:12.5px;font-weight:700;color:${theme.textMain};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`;
const MemberInst = styled.div`font-size:10.5px;color:${theme.textMuted};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`;
const YouBadge = styled.span`font-size:9px;font-weight:800;background:${theme.primaryLight};color:${theme.primary};border-radius:4px;padding:1px 5px;text-transform:uppercase;letter-spacing:.05em;`;
const MembersEmpty = styled.div`font-size:13px;color:${theme.textMuted};text-align:center;padding:16px 0;`;

/* ── Sidebar middle / stats ── */
const SidebarMiddle = styled.div`
  flex: 1; display: flex; flex-direction: column; justify-content: center;
  overflow: hidden; min-height: 0;
`;
const StatsWidget = styled.div`margin: 0 10px; padding: 0 0 10px;`;
const StatsWidgetLabel = styled.div`
  font-size: 9.5px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase;
  color: ${theme.textMuted}; padding: 0 4px; margin-bottom: 10px;
`;
const QuickStatsBox = styled.div`
  background: ${theme.cardBg}; border: 1px solid ${theme.cardBorder}; border-radius: 10px; padding: 12px 12px 6px;
`;
const QuickStatsTitle = styled.div`
  font-size: 9.5px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase;
  color: ${theme.textMain}; margin-bottom: 10px;
`;
const StatRow = styled.div`margin-bottom: 10px;`;
const StatRowTop = styled.div`display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;`;
const StatRowLabel = styled.span`font-size: 11.5px; color: ${theme.textSub}; font-weight: 500;`;
const StatRowValue = styled.span`font-size: 12px; font-weight: 800; color: ${theme.textMain};`;
const StatBar = styled.div`height: 4px; border-radius: 2px; background: ${theme.bgAlt}; overflow: hidden;`;
const StatBarFill = styled.div`
  height: 100%; border-radius: 2px; background: ${theme.primary};
  width: ${p => p.$pct}%; transition: width .6s ease;
`;
const InlineStatRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 5px 0; border-top: 1px solid ${theme.separator};
`;
const InlineStatLabel = styled.span`font-size: 11.5px; color: ${theme.textSub}; font-weight: 500;`;
const InlineStatValue = styled.span`font-size: 12px; font-weight: 700; color: ${theme.primary};`;

/* ── Toast ── */
const Toast = styled.div`
  position:fixed;bottom:28px;left:50%;transform:translateX(-50%);
  color:#fff;border-radius:10px;padding:10px 22px;font-size:13px;font-weight:700;z-index:300;
  display:flex;align-items:center;gap:8px;box-shadow:0 4px 20px rgba(0,0,0,.15);
  animation:${fadeUp} .25s ease both;
`;

/* ── Comments ── */
const CommentSection = styled.div`
  background: ${theme.bgAlt};
  border-radius: 0 0 12px 12px;
  border: 1.5px solid ${theme.cardBorder};
  border-top: none;
  padding: 14px 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: -6px;
`;
const CommentInputRow = styled.form`display: flex; gap: 10px;`;
const CommentInput = styled.input`
  flex: 1; background: ${theme.cardBg}; border: 1.5px solid ${theme.cardBorder};
  border-radius: 7px; padding: 8px 14px; font-size: 13px; font-family: inherit;
  color: ${theme.textMain}; outline: none;
  &::placeholder { color: ${theme.textMuted}; }
  &:focus { border-color: ${theme.primary}; box-shadow: 0 0 0 3px ${theme.primaryGlow}; }
`;
const SendButton = styled.button`
  background: ${theme.primary}; color: #fff; border: none; border-radius: 7px;
  padding: 8px 18px; font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
  &:hover:not(:disabled) { background: ${theme.primaryDark}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const CommentList = styled.div`display: flex; flex-direction: column; gap: 10px;`;
const CommentNote = styled.p`font-size: 13px; color: ${theme.textMuted};`;

function InlineComments({ noteId, onCommentAdded }) {
  const currentUser = useSelector(s => s.user.currentUser);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    publicRequest.get(`/comments/${noteId}`)
      .then(res => { if (!cancelled) setComments(res.data); })
      .catch(() => { if (!cancelled) setError("Failed to load comments."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [noteId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      const res = await publicRequest.post("/comments", { userId: currentUser._id, noteId, text: text.trim() });
      setComments(prev => [{ ...res.data, userId: { _id: currentUser._id, username: currentUser.username } }, ...prev]);
      if (onCommentAdded) onCommentAdded();
      setText("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to post comment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CommentSection>
      {currentUser && (
        <CommentInputRow onSubmit={handleSubmit}>
          <CommentInput type="text" placeholder="Write a comment…" value={text} onChange={e => setText(e.target.value)} />
          <SendButton type="submit" disabled={submitting || !text.trim()}>{submitting ? "…" : "Post"}</SendButton>
        </CommentInputRow>
      )}
      {error && <CommentNote style={{ color: theme.danger }}>{error}</CommentNote>}
      <CommentList>
        {loading ? <CommentNote>Loading…</CommentNote>
          : comments.length === 0 ? <CommentNote>No comments yet.</CommentNote>
          : comments.map(c => <Comment key={c._id} comment={c} />)}
      </CommentList>
    </CommentSection>
  );
}

/* ════════════════════════════════════════════════════════════ */
export default function Community() {
  const currentUser = useSelector(s => s.user.currentUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [communities, setCommunities] = useState([]);
  const [myCommunity, setMyCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // detail view state
  const [activeCommunity, setActiveCommunity] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [toast, setToast] = useState(null);
  const [commentCounts, setCommentCounts] = useState({});
  const [openCommentNoteId, setOpenCommentNoteId] = useState(null);
  const [myNotes, setMyNotes] = useState([]);
  const [communityMenuOpen, setCommunityMenuOpen] = useState(false);

  const avatarSrc = currentUser?.profilePicture ? `${STATIC_URL}/images/${currentUser.profilePicture}` : null;
  const totalLikes = myNotes.reduce((s, n) => s + (n.likes?.length || 0), 0);

  const memberIds = members.map((m) => m._id);
  const onlineSet = useOnlineStatus(currentUser?._id, memberIds);

  /* fetch all communities */
  useEffect(() => {
    const run = async () => {
      setLoading(true); setError(null);
      try { const r = await publicRequest.get("/community"); setCommunities(r.data); }
      catch { setError("Failed to load communities."); }
      finally { setLoading(false); }
    };
    run();
  }, []);

  /* fetch user's own notes for quick stats */
  useEffect(() => {
    if (!currentUser) return;
    publicRequest.get(`/notes/profile/${currentUser._id}`)
      .then(res => setMyNotes(res.data))
      .catch(() => {});
  }, [currentUser]);

  /* auto-fetch/create user's own community */
  useEffect(() => {
    if (!currentUser?.institution) return;
    publicRequest.get(`/community/institution/${encodeURIComponent(currentUser.institution)}`)
      .then(r => {
        setMyCommunity(r.data);
        setCommunities(prev => prev.find(c => c._id === r.data._id) ? prev : [r.data, ...prev]);
      }).catch(() => {});
  }, [currentUser]);

  /* open a community detail */
  const openCommunity = async (community) => {
    setActiveCommunity(community);
    // fetch members
    setMembersLoading(true);
    try { const r = await publicRequest.get(`/community/${community._id}/members`); setMembers(r.data); }
    catch { setMembers([]); }
    finally { setMembersLoading(false); }
    // fetch notes
    setNotesLoading(true);
    setOpenCommentNoteId(null);
    setCommentCounts({});
    try {
      const r = await publicRequest.get(`/community/${community._id}/notes`);
      setNotes(r.data);
      // fetch comment counts for all notes
      const counts = {};
      await Promise.all(r.data.map(async (note) => {
        try { const cr = await publicRequest.get(`/comments/${note._id}`); counts[note._id] = cr.data.length; }
        catch { counts[note._id] = 0; }
      }));
      setCommentCounts(counts);
    } catch { setNotes([]); }
    finally { setNotesLoading(false); }
  };

  /* sync activeCommunity when communities list updates */
  useEffect(() => {
    if (!activeCommunity) return;
    const fresh = communities.find(c => c._id === activeCommunity._id);
    if (fresh && fresh !== activeCommunity) setActiveCommunity(fresh);
  }, [communities, activeCommunity]);

  const isMember = (community) => community?.members?.some(m => m === currentUser?._id || m?._id === currentUser?._id || m?.toString() === currentUser?._id);

  const isLTCECommunity = (community) => {
    const target = `${community?.name || ""} ${community?.institution || ""}`.toUpperCase();
    return target.includes("LTCE");
  };

  const getCommunityLogo = (community) => isLTCECommunity(community) ? ltceLogo : communityImg;
  const getCommunityBanner = (community) => isLTCECommunity(community) ? ltceBannerImg : communityImg;

  const communityNavList = (() => {
    const items = [];
    if (myCommunity) items.push(myCommunity);
    communities.forEach((c) => {
      if (isMember(c) && !items.find((x) => x._id === c._id)) items.push(c);
    });
    return items;
  })();

  const handleJoin = async () => {
    if (!currentUser || !activeCommunity) return;
    setJoining(true);
    try {
      const r = await publicRequest.post(`/community/join/${activeCommunity._id}`, { userId: currentUser._id });
      const updated = r.data.community;
      setCommunities(prev => prev.map(c => c._id === updated._id ? updated : c));
      if (myCommunity?._id === updated._id) setMyCommunity(updated);
      setActiveCommunity(updated);
      // refresh members
      const mr = await publicRequest.get(`/community/${updated._id}/members`);
      setMembers(mr.data);
      showToast(`Joined ${updated.name}!`);
    } catch { showToast("Failed to join.", true); }
    finally { setJoining(false); }
  };

  const handleLeave = async () => {
    if (!currentUser || !activeCommunity) return;
    setJoining(true);
    try {
      const r = await publicRequest.post(`/community/leave/${activeCommunity._id}`, { userId: currentUser._id });
      const updated = r.data.community;
      setCommunities(prev => prev.map(c => c._id === updated._id ? updated : c));
      if (myCommunity?._id === updated._id) setMyCommunity(updated);
      setActiveCommunity(updated);
      const mr = await publicRequest.get(`/community/${updated._id}/members`);
      setMembers(mr.data);
      showToast(`Left ${updated.name}.`);
    } catch { showToast("Failed to leave.", true); }
    finally { setJoining(false); }
  };

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3000);
  };

  return (
    <Shell>
      {/* ── Left sidebar ── */}
      <Sidebar>
        <SidebarTop>
          <LogoRow to="/"><LogoMark>N</LogoMark><div><LogoText>NotesHub</LogoText></div></LogoRow>
        </SidebarTop>
        <Nav>
          <NavItem to="/" $active={location.pathname === "/"}><IconDocument size="15px" /> Feed</NavItem>
          {currentUser && <NavItem to="/folders" $active={location.pathname === "/folders"}><IconFolder size="15px" /> Folders</NavItem>}
          <CommunityMainNavItem to="/community" $active={location.pathname === "/community"}>
            <IconUsers size="15px" /> Community
            <CommunityChevronHit
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCommunityMenuOpen((v) => !v);
              }}
              aria-label="Toggle my communities"
              title="Toggle my communities"
            >
              <CommunityChevron $open={communityMenuOpen} />
            </CommunityChevronHit>
          </CommunityMainNavItem>
          {communityMenuOpen && (
            <CommunitySubList>
              {communityNavList.length === 0 ? (
                <CommunitySubItem type="button" disabled style={{ opacity: 0.6, cursor: "default" }}>
                  No communities yet
                </CommunitySubItem>
              ) : communityNavList.map((c) => (
                <CommunitySubItem
                  key={c._id}
                  type="button"
                  $active={activeCommunity?._id === c._id}
                  onClick={() => openCommunity(c)}
                >
                  <CommunitySubAvatar src={getCommunityLogo(c)} alt={`Logo - ${c.institution || c.name}`} />
                  {c.name}
                </CommunitySubItem>
              ))}
            </CommunitySubList>
          )}
        </Nav>
        <SidebarMiddle>
          {currentUser && (
            <StatsWidget>
              <StatsWidgetLabel>Quick Stats</StatsWidgetLabel>
              <QuickStatsBox>
                <QuickStatsTitle>Quick Stats</QuickStatsTitle>
                <StatRow>
                  <StatRowTop>
                    <StatRowLabel>Notes Shared</StatRowLabel>
                    <StatRowValue>{myNotes.length}</StatRowValue>
                  </StatRowTop>
                  <StatBar><StatBarFill $pct={Math.min(myNotes.length * 10, 100)} /></StatBar>
                </StatRow>
                <InlineStatRow>
                  <InlineStatLabel>Followers</InlineStatLabel>
                  <InlineStatValue>{currentUser.followers?.length ?? 0}</InlineStatValue>
                </InlineStatRow>
                <InlineStatRow>
                  <InlineStatLabel>Total Likes</InlineStatLabel>
                  <InlineStatValue>{totalLikes}</InlineStatValue>
                </InlineStatRow>
              </QuickStatsBox>
            </StatsWidget>
          )}
        </SidebarMiddle>
        {currentUser && (
          <SidebarUser>
            <UserAvatar onClick={() => navigate(`/profile/${currentUser._id}`)}>
              {avatarSrc ? <UserAvatarImg src={avatarSrc} alt={currentUser.username} /> : currentUser.username?.[0]?.toUpperCase()}
            </UserAvatar>
            <UserInfo><UserName>{currentUser.username}</UserName><UserRole>Student</UserRole></UserInfo>
            <LogoutBtn onClick={() => dispatch(logout())} title="Sign out"><IconLogOut size="15px" /></LogoutBtn>
          </SidebarUser>
        )}
      </Sidebar>

      {/* ── Main ── */}
      <Main>
        <Navbar />
        <PageArea>
          {activeCommunity ? (
            /* ══ DETAIL VIEW ══ */
            <DetailShell>
              <DetailMain>
                {/* Header card */}
                <DetailHeader>
                  <DetailHeaderImg>
                    <DetailHeaderImgEl src={getCommunityBanner(activeCommunity)} alt={activeCommunity.name} />
                    <DetailHeaderOverlay />
                  </DetailHeaderImg>
                  <DetailHeaderBody>
                    <DetailHeaderInfo>
                      <DetailInstitution>{activeCommunity.institution}</DetailInstitution>
                      <DetailTitleRow>
                        <DetailLogo src={getCommunityLogo(activeCommunity)} alt={`Logo - ${activeCommunity.institution || activeCommunity.name}`} />
                        <DetailTitle>{activeCommunity.name}</DetailTitle>
                      </DetailTitleRow>
                      <DetailDesc>
                        {activeCommunity.description || `A community for students of ${activeCommunity.institution} to share notes and collaborate.`}
                      </DetailDesc>
                    </DetailHeaderInfo>
                    <DetailActions>
                      <BackBtn onClick={() => setActiveCommunity(null)}><IconX size="14px" /> Back</BackBtn>
                      <JoinBtn
                        $joined={isMember(activeCommunity)}
                        disabled={joining}
                        onClick={isMember(activeCommunity) ? handleLeave : handleJoin}
                      >
                        {isMember(activeCommunity)
                          ? <><IconCheck size="14px" /> Joined</>
                          : <><IconUsers size="14px" /> Join</>}
                      </JoinBtn>
                    </DetailActions>
                  </DetailHeaderBody>
                </DetailHeader>

                {/* Notes feed */}
                <SectionLabel>
                  Community Notes &nbsp;·&nbsp; {notesLoading ? "…" : notes.length}
                </SectionLabel>
                {notesLoading ? (
                  <StatusText>Loading notes…</StatusText>
                ) : notes.length === 0 ? (
                  <StatusText>No notes shared yet in this community.</StatusText>
                ) : (
                  <NotesGrid>
                    {notes.map(note => (
                      <div key={note._id}>
                        <NoteCard
                          note={note}
                          commentCount={commentCounts[note._id] ?? 0}
                          onCommentClick={(id) => setOpenCommentNoteId(p => p === id ? null : id)}
                        />
                        {openCommentNoteId === note._id && (
                          <InlineComments
                            noteId={note._id}
                            onCommentAdded={() => setCommentCounts(p => ({ ...p, [note._id]: (p[note._id] || 0) + 1 }))}
                          />
                        )}
                      </div>
                    ))}
                  </NotesGrid>
                )}
              </DetailMain>

              {/* ── Members sidebar ── */}
              <MembersSidebar>
                <MembersCard>
                  <MembersTitle>
                    <IconUsers size="12px" style={{ verticalAlign: "middle", marginRight: 5 }} />
                    Members &nbsp;·&nbsp; {membersLoading ? "…" : members.length}
                  </MembersTitle>
                  {membersLoading ? (
                    <MembersEmpty>Loading…</MembersEmpty>
                  ) : members.length === 0 ? (
                    <MembersEmpty>No members yet.</MembersEmpty>
                  ) : (
                    <MemberList>
                      {members.map(m => {
                        const mAvatar = m.profilePicture ? `${STATIC_URL}/images/${m.profilePicture}` : null;
                        const isYou = m._id === currentUser?._id;
                        return (
                          <MemberRow key={m._id} onClick={() => navigate(`/profile/${m._id}`)}>
                            <MemberAvatar>
                              {mAvatar ? <MemberAvatarImg src={mAvatar} alt={m.username} /> : m.username?.[0]?.toUpperCase()}
                              <OnlineDot online={onlineSet.has(m._id)} size="9px" borderColor={theme.cardBg} />
                            </MemberAvatar>
                            <MemberInfo>
                              <MemberName>
                                {m.username}
                                {isYou && <> &nbsp;<YouBadge>You</YouBadge></>}
                              </MemberName>
                              <MemberInst>{m.institution || "Student"}</MemberInst>
                            </MemberInfo>
                          </MemberRow>
                        );
                      })}
                    </MemberList>
                  )}
                </MembersCard>
              </MembersSidebar>
            </DetailShell>
          ) : (
            /* ══ LIST VIEW ══ */
            <>
              <PageMeta>Student Network</PageMeta>
              <PageHeading>Communities</PageHeading>
              <PageSub>Connect with students from your institution and beyond.</PageSub>

              {myCommunity && (
                <YourCommunityBanner onClick={() => openCommunity(myCommunity)}>
                  <BannerImg src={getCommunityLogo(myCommunity)} alt={`Logo - ${myCommunity.institution || myCommunity.name}`} />
                  <BannerInfo>
                    <BannerLabel>Your Community</BannerLabel>
                    <BannerName>{myCommunity.name}</BannerName>
                    <BannerMembers>{myCommunity.members?.length ?? 0} member{myCommunity.members?.length !== 1 ? "s" : ""}</BannerMembers>
                  </BannerInfo>
                  <BannerAction>{isMember(myCommunity) ? "View →" : "Join →"}</BannerAction>
                </YourCommunityBanner>
              )}

              {loading ? (
                <StatusText>Loading communities…</StatusText>
              ) : error ? (
                <StatusText $error>{error}</StatusText>
              ) : communities.length === 0 ? (
                <StatusText>No communities yet.</StatusText>
              ) : (
                <Grid>
                  {communities.map(c => (
                    <CommunityCard key={c._id} $active={activeCommunity?._id === c._id} onClick={() => openCommunity(c)}>
                      <CardBody>
                        {(() => {
                          const preview = (c.memberPreview || []).slice(0, 5);
                          const memberCount = typeof c.memberCount === "number" ? c.memberCount : (c.members?.length || 0);
                          const overflow = Math.max(memberCount - preview.length, 0);

                          return (
                            <>
                        <CardTop>
                          <CardLogoWrap>
                            <CardLogo src={getCommunityLogo(c)} alt={`Logo - ${c.institution || c.name}`} />
                          </CardLogoWrap>
                          <div>
                            <CardInstitution>{c.institution}</CardInstitution>
                            <CardName style={{ marginBottom: 0 }}>{c.name}</CardName>
                          </div>
                        </CardTop>
                        <CardMeta>
                          <MemberBadge><IconUsers size="11px" />{memberCount} member{memberCount !== 1 ? "s" : ""}</MemberBadge>
                          {isMember(c) && (
                            <JoinedMeta>
                              <JoinedText>{preview.length > 0 ? "You +" : "You"}</JoinedText>
                              {preview.length > 0 && (
                                <CardMemberStack>
                                  {preview.map((m) => (
                                    <CardMemberAvatar key={m.userId} title={m.username || "User"}>
                                      {m.profilePicture
                                        ? <CardMemberAvatarImg src={`${STATIC_URL}/images/${m.profilePicture}`} alt={m.username || "User"} />
                                        : (m.username?.[0] || "U").toUpperCase()}
                                    </CardMemberAvatar>
                                  ))}
                                  {overflow > 0 && <CardMemberMore>+{overflow}</CardMemberMore>}
                                </CardMemberStack>
                              )}
                              <JoinedText>have joined</JoinedText>
                            </JoinedMeta>
                          )}
                        </CardMeta>
                            </>
                          );
                        })()}
                      </CardBody>
                    </CommunityCard>
                  ))}
                </Grid>
              )}
            </>
          )}
        </PageArea>
      </Main>

      {toast && (
        <Toast style={{ background: toast.isError ? theme.danger : theme.success }}>
          <IconCheck size="14px" />{toast.msg}
        </Toast>
      )}
    </Shell>
  );
}
